import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useScopedNavigate } from '../components/MarketerView/useScopedNavigate';
import { FormattedMessage } from 'react-intl';
import {
    Card,
    Tabs,
    Button,
    Typography,
    Space,
    Statistic,
    Row,
    Col,
    Spin,
    Alert,
    Breadcrumb
} from 'antd';
import {
    ArrowLeftOutlined,
    BarChartOutlined,
    TeamOutlined,
    ReloadOutlined,
    DashboardOutlined,
    MessageOutlined
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import {
    useGetSurveyResponsesQuery,
    useGetSurveyResponseSummaryQuery,
    useGetSurveysQuery
} from '../services/nestjs-base-api';
import ResponsesList from '../components/Survey/ResponsesList';
import ResponseAnalytics from '../components/Survey/ResponseAnalytics';
import QuestionCoverageDashboard from '../components/Survey/QuestionCoverageDashboard';
import OpenResponses from '../components/Survey/OpenResponses';

const { Title, Text } = Typography;

const SurveyResultsPage: React.FC = () => {
    const { surveyHash } = useParams<{ surveyHash: string }>();
    const navigate = useScopedNavigate();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');

    const { data: surveys, isLoading: surveysLoading } = useGetSurveysQuery({
        jwtToken: user!.jwtToken,
        organisationId: user!.organisation.id
    });

    const survey = surveys?.find(s => s.hash === surveyHash);
    const surveyId = survey?.id;

    const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useGetSurveyResponseSummaryQuery({
        jwtToken: user!.jwtToken,
        surveyId: surveyId!
    }, { skip: !surveyId });

    const { data: responses, isLoading: responsesLoading, refetch: refetchResponses } = useGetSurveyResponsesQuery({
        jwtToken: user!.jwtToken,
        surveyId: surveyId!
    }, { skip: !surveyId });

    if (surveysLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!survey) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Alert
                    message={<FormattedMessage id="survey.results.notFound" />}
                    description={<FormattedMessage id="survey.results.notFoundDesc" />}
                    type="warning"
                    showIcon
                    action={
                        <Button type="primary" onClick={() => navigate('/dashboard/surveys')}>
                            <FormattedMessage id="survey.results.backToSurveys" />
                        </Button>
                    }
                />
            </div>
        );
    }


    const handleRefresh = async () => {
        try {
            await Promise.all([
                refetchResponses(),
                refetchSummary()
            ]);
            // Success message will be shown automatically by the UI updates
        } catch (error) {
            console.error('Refresh failed:', error);
        }
    };


    const tabItems = [
        {
            key: 'overview',
            label: (
                <span>
                    <BarChartOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="survey.results.tabs.overview" />
                </span>
            ),
            children: (
                <ResponseAnalytics
                    responses={responses || []}
                    summary={summary}
                    loading={responsesLoading || summaryLoading}
                    survey={survey}
                />
            )
        },
        {
            key: 'responses',
            label: (
                <span>
                    <TeamOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="survey.results.tabs.responses" />
                </span>
            ),
            children: (
                <ResponsesList
                    responses={responses || []}
                    loading={responsesLoading}
                    survey={survey}
                    onResponseDeleted={() => {
                        refetchResponses();
                        refetchSummary();
                    }}
                />
            )
        },
        {
            key: 'coverage',
            label: (
                <span>
                    <DashboardOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="survey.results.tabs.coverage" />
                </span>
            ),
            children: (
                <QuestionCoverageDashboard
                    surveyId={survey.id}
                />
            )
        },
        {
            key: 'openResponses',
            label: (
                <span>
                    <MessageOutlined style={{ marginRight: 8 }} />
                    Open Responses
                </span>
            ),
            children: (
                <OpenResponses
                    survey={survey}
                    loading={responsesLoading || summaryLoading}
                />
            )
        }
    ];

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ marginBottom: '24px' }}>
                <Breadcrumb
                    items={[
                        {
                            title: <FormattedMessage id="section.surveys" />
                        },
                        {
                            title: survey.name
                        },
                        {
                            title: <FormattedMessage id="survey.results.title" />
                        }
                    ]}
                />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <Button
                        icon={<ArrowLeftOutlined style={{ marginRight: 8 }} />}
                        onClick={() => navigate(`/dashboard/surveys/${surveyHash}`)}
                        style={{ marginBottom: '16px' }}
                    >
                        <FormattedMessage id="common.back" />
                    </Button>
                    <Title level={2} style={{ margin: 0 }}>
                        <FormattedMessage id="survey.results.title" />
                    </Title>
                    <Text type="secondary" style={{ fontSize: '16px' }}>
                        {survey.name} • {survey.brand?.name || <FormattedMessage id="survey.results.noBrand" />}
                    </Text>
                </div>

                <Space>
                    <Button 
                        icon={<ReloadOutlined />} 
                        loading={responsesLoading || summaryLoading}
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Space>
            </div>

            {summary && (
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title={<FormattedMessage id="survey.results.stats.totalResponses" />}
                                value={summary.totalResponses}
                                suffix={<FormattedMessage id="survey.results.stats.responses" />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title={<FormattedMessage id="survey.results.stats.completedResponses" />}
                                value={summary.completedResponses}
                                suffix={<FormattedMessage id="survey.results.stats.completed" />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title={<FormattedMessage id="survey.results.stats.completionRate" />}
                                value={summary.completionRate}
                                precision={1}
                                suffix="%"
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title={<FormattedMessage id="survey.results.stats.linkAccesses" />}
                                value={summary.totalAccesses || 0}
                                suffix={<FormattedMessage id="survey.results.stats.accesses" />}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            <Card>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                />
            </Card>
        </div>
    );
};

export default SurveyResultsPage;